import {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  signOut,
  deleteUser,
  signInWithRedirect,
  fetchAuthSession,
  updateUserAttributes,
  updateUserAttribute,
  confirmUserAttribute,
  type SignInOutput,
  type SignUpOutput,
  type ConfirmSignUpOutput,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from '@aws-amplify/auth/cognito';
import { clearCredentials, Hub } from '@aws-amplify/core';
import { AMPLIFY_SYMBOL } from '@aws-amplify/core/internals/utils';
import type {
  IdentityProvider,
  IdpSignInResult,
  IdpSignUpResult,
  IdpConfirmSignUpResult,
  IdpSignUpAttributes,
  IdpUpdateProfileAttributes,
} from '../identity-provider';
import type { CognitoConfig } from '../cognito-config';
import type { AuthLogger } from '../../shared/auth-logger';
import { IdpSignInStep, IdpEvent, ProbeStatus } from '../../constants';
import { IdpErrorName, AmplifySignInStep, AmplifyAuthFlow } from '../cognito/errors';

/** Amplify Hub event fired when the OAuth code exchange succeeds. */
export const HUB_EVENT_SIGN_IN_WITH_REDIRECT = 'signInWithRedirect';
/** Amplify Hub event fired when the OAuth code exchange fails. */
export const HUB_EVENT_SIGN_IN_WITH_REDIRECT_FAILURE = 'signInWithRedirect_failure';
/** Amplify Hub event carrying the `customState` passed to `signInWithRedirect`. */
export const HUB_EVENT_CUSTOM_OAUTH_STATE = 'customOAuthState';

const SRP_A_HEX_LENGTH = 512;

export class AmplifyIdentityProvider implements IdentityProvider {
  private readonly cognitoConfig: CognitoConfig;
  private readonly tenant: string;

  constructor(cognitoConfig: CognitoConfig, tenant: string) {
    this.cognitoConfig = cognitoConfig;
    this.tenant = tenant;
  }

  private get clientMetadata(): Record<string, string> {
    const meta: Record<string, string> = { tenant: this.tenant };
    if (typeof document !== 'undefined') {
      const lang = document.documentElement.lang;
      if (lang) meta.locale = lang;
    }
    return meta;
  }

  async signIn(email: string, password: string): Promise<IdpSignInResult> {
    const output: SignInOutput = await signIn({ username: email, password, options: { authFlowType: AmplifyAuthFlow.UserPasswordAuth } });
    const step = output.nextStep.signInStep;
    if (step === AmplifySignInStep.ConfirmSignUp) return { step: IdpSignInStep.RequiresEmailVerification };
    if (step === AmplifySignInStep.NewPasswordRequired) return { step: IdpSignInStep.RequiresNewPassword };
    return { step: IdpSignInStep.SignInComplete };
  }

  async signUp(email: string, password: string, attrs: IdpSignUpAttributes): Promise<IdpSignUpResult> {
    const output: SignUpOutput = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email: attrs.email,
          given_name: attrs.givenName,
          family_name: attrs.familyName,
          ...(attrs.phoneNumber ? { phone_number: attrs.phoneNumber } : {}),
          'custom:tenant': this.tenant,
        },
        clientMetadata: this.clientMetadata,
      },
    });
    return { complete: output.isSignUpComplete };
  }

  async confirmSignUp(email: string, code: string): Promise<IdpConfirmSignUpResult> {
    const output: ConfirmSignUpOutput = await confirmSignUp({ username: email, confirmationCode: code, options: { clientMetadata: this.clientMetadata } });
    return { complete: output.isSignUpComplete };
  }

  async resendSignUpCode(email: string): Promise<void> {
    await resendSignUpCode({ username: email, options: { clientMetadata: this.clientMetadata } });
  }

  async resetPassword(email: string): Promise<void> {
    await resetPassword({ username: email, options: { clientMetadata: this.clientMetadata } });
  }

  async confirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
    await confirmResetPassword({ username: email, confirmationCode: code, newPassword, options: { clientMetadata: this.clientMetadata } });
  }

  async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
    await updatePassword({ oldPassword: currentPassword, newPassword });
  }

  async signOut(redirectUrl?: string): Promise<void> {
    await signOut(redirectUrl ? { global: false, oauth: { redirectUrl }} : { global: false });
  }

  async signOutLocally(): Promise<void> {
    const { tokenOrchestrator } = cognitoUserPoolsTokenProvider;
    tokenOrchestrator.clearTokens();
    await clearCredentials();
    Hub.dispatch('auth', { event: 'signedOut' }, 'Auth', AMPLIFY_SYMBOL);
  }

  async deleteUser(): Promise<void> {
    await deleteUser();
  }

  async socialRedirect(provider: 'Google' | 'Facebook' | 'Apple', customState?: string): Promise<void> {
    await signInWithRedirect({
      provider,
      customState,
      options: provider === 'Google' ? { prompt: 'SELECT_ACCOUNT' } : undefined,
    });
  }

  async getIdToken(): Promise<string> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('No active session: ID token unavailable.');    
    return token;
  }

  async getTokenEmail(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const email = session.tokens?.idToken?.payload?.['email'];
      return typeof email === 'string' ? email : null;
    } catch {
      return null;
    }
  }

  async getTokenProfile(): Promise<{ firstName: string | null; lastName: string | null }> {
    try {
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload;
      const firstName = typeof payload?.['given_name'] === 'string' ? payload['given_name'] : null;
      const lastName = typeof payload?.['family_name'] === 'string' ? payload['family_name'] : null;
      return { firstName, lastName };
    } catch {
      return { firstName: null, lastName: null };
    }
  }

  async getSignInProvider(logger?: AuthLogger): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      const identities = session.tokens?.idToken?.payload?.['identities'];
      if (Array.isArray(identities) && identities.length > 0) {
        const first = identities[0] as { providerName?: string };
        const providerName = first.providerName ?? null;
        logger?.log(IdpEvent.GetSignInProvider, { provider: providerName });
        return providerName;
      }
      logger?.log(IdpEvent.GetSignInProvider, { provider: null });
      return null;
    } catch {
      logger?.log(IdpEvent.GetSignInProviderNoSession);
      return null;
    }
  }

  async updateProfileAttributes(attrs: IdpUpdateProfileAttributes): Promise<void> {
    const userAttributes: Record<string, string> = {};
    if (attrs.givenName !== undefined) userAttributes['given_name'] = attrs.givenName;
    if (attrs.familyName !== undefined) userAttributes['family_name'] = attrs.familyName;
    if (attrs.phoneNumber !== undefined) userAttributes['phone_number'] = attrs.phoneNumber;
    if (Object.keys(userAttributes).length > 0) {
      await updateUserAttributes({ userAttributes });
    }
  }

  async updateEmailAttribute(newEmail: string): Promise<void> {
    await updateUserAttribute({ userAttribute: { attributeKey: 'email', value: newEmail } });
  }

  async confirmEmailAttribute(code: string): Promise<void> {
    await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode: code });
  }

  async probeUserStatus(email: string): Promise<ProbeStatus> {
    const cognito = this.cognitoConfig;
    if (!cognito.region || !cognito.clientId) {
      return ProbeStatus.AccountReady;
    }

    const randomBytes = new Uint8Array(SRP_A_HEX_LENGTH / 2);
    crypto.getRandomValues(randomBytes);
    const srpA = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');

    try {
      const response = await fetch(`https://cognito-idp.${cognito.region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
          'Content-Type': 'application/x-amz-json-1.1',
        },
        body: JSON.stringify({
          AuthFlow: AmplifyAuthFlow.UserSrpAuth,
          ClientId: cognito.clientId,
          AuthParameters: {
            USERNAME: email,
            SRP_A: srpA,
          },
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const errorType = (body as { __type?: string }).__type ?? '';
        if (errorType === IdpErrorName.PasswordResetRequired) {
          return ProbeStatus.RequiresPasswordReset;
        }
        if (errorType === IdpErrorName.UserNotConfirmed) {
          return ProbeStatus.RequiresEmailVerification;
        }
        if (errorType === IdpErrorName.UserNotFound) {
          return ProbeStatus.UserNotFound;
        }
        return ProbeStatus.Unknown;
      }

      return ProbeStatus.AccountReady;
    } catch {
      return ProbeStatus.Unknown;
    }
  }

  onOAuthRedirectComplete(onSuccess: () => void, onError: (error: string) => void): () => void {
    const unsub = Hub.listen('auth', ({ payload }) => {
      if (payload.event === HUB_EVENT_SIGN_IN_WITH_REDIRECT) {
        unsub();
        onSuccess();
      }
      if (payload.event === HUB_EVENT_SIGN_IN_WITH_REDIRECT_FAILURE) {
        unsub();
        const data = payload.data as unknown as { error?: { message?: string } } | undefined;
        onError(data?.error?.message ?? 'Social sign-in failed');
      }
    });
    return unsub;
  }
}
